import { Crown, X } from "lucide-react";

const MODAL_OVERLAY_STYLE = {
    background: "rgba(0,0,0,0.7)",
    backdropFilter: "blur(6px)",
};

const PLAN_BADGE_STYLES = {
    free: "bg-gray-500/20 text-gray-300",
    growth: "bg-brand-500/20 text-brand-300",
    pro: "bg-purple-500/20 text-purple-300",
    business: "bg-yellow-500/20 text-yellow-300",
};

export function PlanBadge({ plan }) {
    const cls = PLAN_BADGE_STYLES[plan] || PLAN_BADGE_STYLES.free;
    return (
        <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${cls}`}>{plan || "free"}</span>
    );
}

function Box({ label, v }) {
    return (
        <div className="bg-white/5 rounded-xl p-3">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</div>
            <div className="text-sm font-semibold truncate">{v}</div>
        </div>
    );
}

function LeadsList({ leads }) {
    if (leads.length === 0) {
        return <p className="text-gray-500 text-sm bg-white/5 rounded-xl p-4 text-center">No leads yet</p>;
    }
    return (
        <div className="space-y-2 mb-6 max-h-60 overflow-y-auto pr-1">
            {leads.slice(0, 50).map((l) => (
                <div key={l.id} className="bg-white/5 rounded-lg px-3 py-2 text-sm flex items-center justify-between">
                    <div className="min-w-0">
                        <div className="font-medium truncate">{l.name}</div>
                        <div className="text-xs text-gray-500">+{l.phone} {l.email && `· ${l.email}`}</div>
                    </div>
                    <div className="text-xs text-gray-500 whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</div>
                </div>
            ))}
        </div>
    );
}

function ScansList({ scans }) {
    if (scans.length === 0) {
        return <p className="text-gray-500 text-sm bg-white/5 rounded-xl p-4 text-center">No scans yet</p>;
    }
    return (
        <div className="space-y-1 max-h-40 overflow-y-auto pr-1 text-xs">
            {scans.slice(0, 20).map((s) => (
                <div key={s.id} className="bg-white/5 rounded px-3 py-1.5 flex justify-between text-gray-400">
                    <span className="truncate max-w-[60%]">{s.user_agent || "—"}</span>
                    <span>{new Date(s.timestamp).toLocaleString()}</span>
                </div>
            ))}
        </div>
    );
}

export default function UserDrillModal({ user, data, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={MODAL_OVERLAY_STYLE} onClick={onClose} data-testid="admin-drill-modal">
            <div className="glass rounded-3xl p-8 max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-brand-500/20 flex items-center justify-center text-brand-400 text-2xl font-bold">
                            {(user.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h2 className="font-display font-bold text-2xl flex items-center gap-2">
                                {user.name}
                                {user.role === "admin" && <Crown className="w-5 h-5 text-yellow-400" />}
                            </h2>
                            <p className="text-sm text-gray-400">{user.business || "(no business)"} · /u/{user.slug}</p>
                            <div className="flex gap-2 mt-2">
                                <PlanBadge plan={user.plan} />
                                <span className="px-2 py-1 rounded text-[10px] font-bold uppercase bg-white/5 text-gray-400">{user.auth_provider}</span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg" data-testid="admin-drill-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-6">
                    <Box label="Phone" v={user.phone ? "+" + user.phone : "—"} />
                    <Box label="Email" v={user.email || "—"} />
                    <Box label="Scans" v={user.total_scans} />
                    <Box label="Leads" v={user.total_leads} />
                </div>

                {!data ? (
                    <p className="text-gray-500 text-sm text-center py-6">Loading activity...</p>
                ) : (
                    <>
                        <h3 className="font-semibold text-sm mb-3 text-gray-300">Recent Leads ({data.leads.length})</h3>
                        <LeadsList leads={data.leads} />
                        <h3 className="font-semibold text-sm mb-3 text-gray-300">Recent Scans ({data.scans.length})</h3>
                        <ScansList scans={data.scans} />
                    </>
                )}
            </div>
        </div>
    );
}
